import { HttpError } from '../../../common/src/server_api.ts';

// How the song list sync retries a failed upload (issue #3609). Kept free of IndexedDB, localStorage and
// server calls so the rules can be unit tested; use_current_org.tsx drives them.

/** The server refused the pending song list updates: its status and its reason (the response text). */
export type SongListSaveRefusal = { status: number; message: string };

export type SongListSyncFailure =
  // offline, a network error, a 5xx, 401 (expired or missing token: signing in again fixes it), 408 or 429:
  // normal for an offline-first app, retry soon and say nothing
  | { kind: 'transient' }
  // the user may not edit this church's song lists: drop the pending updates and take the server's lists
  | { kind: 'forbidden' }
  // any other 4xx (400, 404, 409, 413, 422, ...): the server rejected the content and will keep refusing
  // these updates, so back off and tell the admin
  | { kind: 'refused'; refusal: SongListSaveRefusal };

export const transientRetryDelayMs = 5_000;

/** Delay before retrying after the 1st, 2nd, ... consecutive refusal; the last entry is the cap. */
export const refusedRetryDelaysMs = [5_000, 30_000, 2 * 60_000, 5 * 60_000, 10 * 60_000];

export const maxRefusalMessageLength = 300;

// 4xx statuses that say nothing about the content of the updates
const transientClientErrorStatuses = new Set([401, 408, 429]);

export function classifySongListSyncFailure(error: unknown): SongListSyncFailure {
  if (!(error instanceof HttpError)) {
    return { kind: 'transient' };
  }
  const { status } = error;
  if (status === 403) {
    return { kind: 'forbidden' };
  }
  if (status < 400 || status >= 500 || transientClientErrorStatuses.has(status)) {
    return { kind: 'transient' };
  }
  return { kind: 'refused', refusal: { status, message: makeRefusalMessage(error.responseText) } };
}

/** The banner shows this text: cap its length and redact anything that looks like a credential. */
export function makeRefusalMessage(responseText: string): string {
  const message = redactCredentials(responseText).trim();
  if (message.length <= maxRefusalMessageLength) {
    return message;
  }
  return message.slice(0, maxRefusalMessageLength - 1).trimEnd() + '…';
}

function redactCredentials(text: string): string {
  return text.replace(/\b(Bearer|Basic)\s+[^\s)]+/gi, '$1 [redacted]');
}

/** @param consecutiveRefusals how many attempts in a row the server has refused, counting this one (>= 1) */
export function refusedRetryDelayMs(consecutiveRefusals: number): number {
  const index = Math.min(Math.max(consecutiveRefusals, 1), refusedRetryDelaysMs.length) - 1;
  return refusedRetryDelaysMs[index]!;
}

/**
 * The retry state of the song list sync: the current refusal (if any), the refusal backoff, and the one
 * pending retry timer. Scheduling a retry replaces the pending one, so edits made during a backoff can
 * never stack timers.
 */
export class SongListSyncRetry {
  private currentRefusal: SongListSaveRefusal | undefined;
  private consecutiveRefusals = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  get refusal(): SongListSaveRefusal | undefined {
    return this.currentRefusal;
  }

  /** The pending updates reached the server (or were dropped): clear the refusal and the backoff. */
  recordSuccess() {
    this.currentRefusal = undefined;
    this.consecutiveRefusals = 0;
  }

  /** @returns the delay before the next attempt */
  recordFailure(failure: SongListSyncFailure): number {
    switch (failure.kind) {
      case 'transient':
        // leave any refusal in place: being offline says nothing about whether the server still refuses
        return transientRetryDelayMs;
      case 'forbidden':
        this.recordSuccess();
        return transientRetryDelayMs;
      case 'refused':
        this.currentRefusal = failure.refusal;
        this.consecutiveRefusals++;
        return refusedRetryDelayMs(this.consecutiveRefusals);
    }
  }

  /** A new edit or an explicit retry: start the backoff over (the refusal stays until a save succeeds). */
  resetBackoff() {
    this.consecutiveRefusals = 0;
  }

  scheduleRetry(delayMs: number, retry: () => void) {
    this.cancelRetry();
    this.timer = setTimeout(() => {
      this.timer = undefined;
      retry();
    }, delayMs);
  }

  cancelRetry() {
    if (this.timer === undefined) {
      return;
    }
    clearTimeout(this.timer);
    this.timer = undefined;
  }
}
